import { DatePipe } from '@angular/common'
import { Component } from '@angular/core'
import { RouterLink } from '@angular/router'

import { BaseComponent, FeatureType } from '../../shared'

interface JhaSummary {
    jhasSubmittedLast30Days: number
    openNonCompliantItems: number
    jobsDueCount: number
}

interface VehicleInspectionSummary {
    inspectionsThisMonth: number
    vehiclesOverdueThisMonth: number
}

interface SubcontractorTrainingSummary {
    totalSent: number
    completed: number
    pending: number
    overdue: number
}

interface SafetyFeedbackJobCount {
    jobId: string
    jobName: string
    count: number
}

interface SafetyFeedbackSummary {
    totalSubmitted: number
    submittedThisMonth: number
    submittedLast30Days: number
    jobsWithFeedback: number
    topJobs: SafetyFeedbackJobCount[]
}

interface FeedbackRequiringJhaItem {
    feedbackId: string
    jobId: string
    jobName: string
    eventDateTime: string | null
}

interface FeedbackRequiringJhaSummary {
    needsJhaCount: number
    inProgressCount: number
    addressedThisMonth: number
    outstanding: FeedbackRequiringJhaItem[]
}

interface SafetyDashboardSummary {
    jha: JhaSummary
    vehicleInspection: VehicleInspectionSummary
    subcontractorTraining: SubcontractorTrainingSummary
    safetyFeedback: SafetyFeedbackSummary
    feedbackRequiringJha: FeedbackRequiringJhaSummary
}

const emptySummary = (): SafetyDashboardSummary => ({
    jha: { jhasSubmittedLast30Days: 0, openNonCompliantItems: 0, jobsDueCount: 0 },
    vehicleInspection: { inspectionsThisMonth: 0, vehiclesOverdueThisMonth: 0 },
    subcontractorTraining: { totalSent: 0, completed: 0, pending: 0, overdue: 0 },
    safetyFeedback: { totalSubmitted: 0, submittedThisMonth: 0, submittedLast30Days: 0, jobsWithFeedback: 0, topJobs: [] },
    feedbackRequiringJha: { needsJhaCount: 0, inProgressCount: 0, addressedThisMonth: 0, outstanding: [] },
})

// Merge the API payload over the empty defaults so a missing or partial section can never crash the template
// (e.g. an older API build that doesn't return every section yet).
const mergeSummary = (result: Partial<SafetyDashboardSummary> | null | undefined): SafetyDashboardSummary => {
    const base = emptySummary()
    const r = result && typeof result === 'object' ? (result as SafetyDashboardSummary) : ({} as SafetyDashboardSummary)
    return {
        jha: { ...base.jha, ...(r.jha ?? {}) },
        vehicleInspection: { ...base.vehicleInspection, ...(r.vehicleInspection ?? {}) },
        subcontractorTraining: { ...base.subcontractorTraining, ...(r.subcontractorTraining ?? {}) },
        safetyFeedback: { ...base.safetyFeedback, ...(r.safetyFeedback ?? {}) },
        feedbackRequiringJha: { ...base.feedbackRequiringJha, ...(r.feedbackRequiringJha ?? {}) },
    }
}

@Component({
    selector: 'app-safety-dashboard',
    templateUrl: './safety-dashboard.component.html',
    styleUrls: ['./safety-dashboard.component.css'],
    standalone: true,
    imports: [RouterLink, DatePipe],
})
export class SafetyDashboardComponent extends BaseComponent {
    summary: SafetyDashboardSummary = emptySummary()
    loadError = false

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.isInitialLoading = true
        this.cancelableRequest(
            'safetyDashboardSummary',
            true,
            this.services.data.get<SafetyDashboardSummary>('/api/SafetyDashboard/GetSummary').subscribe({
                next: result => {
                    this.summary = mergeSummary(result)
                    this.loadError = false
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.summary = emptySummary()
                    this.loadError = true
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    get canViewJhaDashboard(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisDashboardView])
    }

    get canViewVehicleInspectionDashboard(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyVehicleInspectionDashboardView])
    }

    get canViewSubcontractorTraining(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SubcontractorTrainingView])
    }

    get canViewSafetyFeedback(): boolean {
        return this.services.authentication.hasFeature([FeatureType.UsersView])
    }
}
